import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  listExecutionInstances,
  type ExecutionListFilters,
  type ExecutionListItem,
  type ExecutionListResult,
  type ExecutionSortField,
  type SortDirection,
} from '../../../services/executionService';
import { executionKeys, buildSortKey } from '../keys';

export interface UseExecutionListParams {
  organizationId?: string;
  filters: ExecutionListFilters;
  page: number;
  pageSize: number;
  sortField: ExecutionSortField;
  sortDirection: SortDirection;
}

interface UseExecutionListResult {
  items: ExecutionListItem[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useExecutionList(params: UseExecutionListParams): UseExecutionListResult {
  const { organizationId, filters, page, pageSize, sortField, sortDirection } = params;
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search ?? '');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(filters.search ?? ''), 350);
    return () => clearTimeout(handler);
  }, [filters.search]);

  const normalizedFilters = useMemo<ExecutionListFilters>(() => ({
    ...filters,
    search: debouncedSearch,
  }), [debouncedSearch, filters.overdue, filters.priority, filters.status, filters.templateId]);

  const filtersHash = useMemo(
    () =>
      JSON.stringify({
        status: normalizedFilters.status ?? [],
        priority: normalizedFilters.priority ?? [],
        templateId: normalizedFilters.templateId ?? null,
        search: normalizedFilters.search ?? '',
        overdue: Boolean(normalizedFilters.overdue),
      }),
    [normalizedFilters],
  );

  const sortKey = useMemo(() => buildSortKey(sortField, sortDirection), [sortField, sortDirection]);

  const queryKey = useMemo(() => {
    if (!organizationId) return executionKeys.lists(undefined);
    return executionKeys.list(organizationId, filtersHash, page, pageSize, sortKey);
  }, [organizationId, filtersHash, page, pageSize, sortKey]);

  const query = useQuery<ExecutionListResult, Error>({
    queryKey,
    enabled: Boolean(organizationId),
    placeholderData: keepPreviousData,
    queryFn: () =>
      listExecutionInstances({
        organizationId: organizationId!,
        page,
        pageSize,
        sortField,
        sortDirection,
        filters: normalizedFilters,
      }),
  });

  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    page: query.data?.page ?? page,
    pageSize: query.data?.pageSize ?? pageSize,
    isLoading: query.isPending && !query.data,
    isFetching: query.isFetching,
    error: query.error ?? null,
    refetch: async () => {
      await query.refetch();
    },
  };
}
